import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import { useEffect, useState } from "react";
import { Button, Container, ListGroup, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Flag from "react-world-flags";
import AttendeeMatchButton from "../components/AttendeeMatchButton";
import BigButton from "../components/events/BigButton";
import Spinner from "../components/Spinner";
import { auth, db } from "../firebase";
import useMatches from "../hooks/useMatches";
import MatchConfirmModal from "../modals/matchConfirmModal";
import MatchStatus from "../components/matches/MatchStatus";
import IceBreaker from "../components/icebreaker/IceBreaker";

const ADMIN_EMAIL = "johnnymoran44@hotmail.com";

export default function EventAttendees() {
	const { eventId } = useParams();
	const [attendees, setAttendees] = useState([]);
	const [loading, setLoading] = useState(true);
	const [matchTarget, setMatchTarget] = useState(null);
	const [matching, setMatching] = useState(false);
	const [showIceBreaker, setShowIceBreaker] = useState(false);
	const [eventCreatedBy, setEventCreatedBy] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const navigate = useNavigate();

	const currentUid = auth.currentUser?.uid;
	const currentEmail = auth.currentUser?.email;

	const isAuthorised = currentUid === eventCreatedBy || currentEmail === ADMIN_EMAIL;

	const {
		iHaveMatchedThem,
		theyHaveMatchedMe,
		isMutualWith,
		match,
		unmatch,
		loading: loadingMatches,
	} = useMatches(currentUid);

	useEffect(() => {
		const fetchEvent = async () => {
			try {
				const eventRef = doc(db, "events", eventId);
				const eventSnap = await getDoc(eventRef);
				if (eventSnap.exists()) {
					const eventData = eventSnap.data();
					setEventCreatedBy(eventData.userId || null);
					if (eventData.attending?.length) {
						const promises = eventData.attending.map(async (uid) => {
							const userRef = doc(db, "users", uid);
							const userSnap = await getDoc(userRef);
							return userSnap.exists() ? { uid, ...userSnap.data() } : null;
						});
						const users = await Promise.all(promises);
						setAttendees(users.filter(Boolean));
					}
				}
			} catch (error) {
				console.error("Error fetching attendees:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchEvent();
	}, [eventId]);

	const handleConfirmMatch = async () => {
		if (!matchTarget) return;
		setMatching(true);
		await match(matchTarget.uid);
		setMatching(false);
		setMatchTarget(null);
	};

	const handleUnmatch = (uid) => unmatch(uid);

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		setDeleting(true);
		try {
			const eventRef = doc(db, "events", eventId);
			await updateDoc(eventRef, {
				attending: arrayRemove(deleteTarget.uid),
			});
			setAttendees((prev) => prev.filter((a) => a.uid !== deleteTarget.uid));
		} catch (error) {
			console.error("Error removing attendee:", error);
		} finally {
			setDeleting(false);
			setDeleteTarget(null);
		}
	};

	if (loading || loadingMatches) return <Spinner />;

	return (
		<Container>
			<BigButton title="Back to Event" url={`/event/${eventId}`} icon="bi bi-arrow-left-circle" />

			<div className="d-flex align-items-center justify-content-between mb-3 mt-3">
				<h3 className="mb-0">Attendees ({attendees.length})</h3>
				<Button className="my-ice" onClick={() => setShowIceBreaker(true)}>
					<i className="bi bi-chat-dots me-2"></i>
					Start Ice Breaker
				</Button>
			</div>

			<ListGroup variant="flush">
				{attendees.map((attendee) => {
					const isCurrentUser = attendee.uid === currentUid;
					const iHaveMatched = iHaveMatchedThem(attendee.uid);
					const theyMatched = theyHaveMatchedMe(attendee.uid);
					const isMutual = isMutualWith(attendee.uid);

					return (
						<ListGroup.Item
							key={attendee.uid}
							className="d-flex py-2 p-0"
							style={{ cursor: "pointer" }}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								navigate(`/profile/${attendee.uid}`);
							}}
						>
							{/* Col 1: Avatar */}
							<img
								src={attendee.photoURL || "/images/no-profile-picture.png"}
								alt={attendee.displayName || "User"}
								className="rounded-circle me-3 flex-shrink-0"
								style={{ width: "50px", height: "50px", objectFit: "cover" }}
							/>

							{/* Col 2: Text */}
							<div className="flex-grow-1 overflow-hidden me-2">
								<div className="fw-bold text-truncate">
									{attendee?.nationality && (
										<Flag
											code={attendee.nationality.code}
											style={{ width: "30px", height: "20px", marginRight: "8px" }}
										/>
									)}
									{attendee.displayName}
								</div>
								<small className="text-muted text-truncate mt-1" style={{ lineHeight: "1.2", display: "block" }}>
									{attendee.interestText || ""}
								</small>
								<MatchStatus
									displayName={attendee.displayName}
									iHaveMatched={iHaveMatched}
									theyMatched={theyMatched}
									isMutual={isMutual}
									isCurrentUser={isCurrentUser}
								/>
							</div>

							{/* Col 3: Buttons */}
							{!isCurrentUser && (
								<div
									className="flex-shrink-0 align-self-center d-flex gap-2"
									style={{ minWidth: "90px", textAlign: "right" }}
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
									}}
								>
									<AttendeeMatchButton
										iHaveMatchedThem={iHaveMatched}
										isMutual={isMutual}
										onMatch={() => setMatchTarget({ uid: attendee.uid, displayName: attendee.displayName })}
										onUnmatch={() => handleUnmatch(attendee.uid)}
									/>
									{isAuthorised && (
										<Button
											variant="outline-danger"
											size="sm"
											onClick={() => setDeleteTarget({ uid: attendee.uid, displayName: attendee.displayName })}
										>
											<i className="bi bi-person-x"></i>
										</Button>
									)}
								</div>
							)}
						</ListGroup.Item>
					);
				})}
			</ListGroup>

			<MatchConfirmModal
				matchTarget={matchTarget}
				onConfirm={handleConfirmMatch}
				onCancel={() => setMatchTarget(null)}
				matching={matching}
			/>

			{/* Delete Attendee Confirmation Modal */}
			<Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Remove Attendee</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Are you sure you want to remove <strong>{deleteTarget?.displayName}</strong> from this event?
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
						{deleting ? "Removing..." : "Yes, Remove"}
					</Button>
				</Modal.Footer>
			</Modal>

			<IceBreaker
				show={showIceBreaker}
				onClose={() => setShowIceBreaker(false)}
				title="Ice Breaker"
				attendees={attendees.map((a) => a.uid)}
			/>
		</Container>
	);
}
